/*
`com-radio-group` allows user to select at most one radio button from a set.
Checking one radio button that belongs to a radio group unchecks any
previously checked radio button within the same group. Use
`selected` to get or set the selected radio button.
The <com-radio> inside the group must have the `name` attribute (attrForSelected can be updated)
set.
Example:
    <com-radio-group selected="small">
      <com-radio name="small">Small</com-radio>
      <com-radio name="medium">Medium</com-radio>
      <com-radio name="large">Large</com-radio>
    </com-radio-group>
*/
/*
  FIXME(polymer-modulizer): the above comments were extracted
  from HTML and may be out of place here. Review them and
  then delete this comment!
*/
import { PolymerElement, html } from '@polymer/polymer';
import './style/com-radio-group-style.js';

class COMRadioGroup extends PolymerElement {
  static get template() {
    return html`
    <style include="com-radio-group-style"></style>
    <slot id="slot" on-slotchange="_updateSlotted"></slot>
`;
  }

  static get is() { return 'com-radio-group'; }

  static get properties() {
    return {

      /**
       * @desc selected value
       * @type {String}
       */
      selected: {
        type: String,
        notify: true
      },

      /**
       * @desc aria role
       * @type {String}
       */
      role: {
        value: 'radiogroup',
        reflectToAttribute: true
      },

      /**
       * @desc property containing the value of a radio
       * @type {String}
       */
      attrForSelected: {
        type: String,
        value: 'name'
      },

      /**
       * @desc property that determines if radio is selected
       * @type {String}
       */
      selectedAttribute: {
        type: String,
        value: 'checked'
      },

      /**
       * @desc selectable item, defaulted, to com-radio, but can be updated to support elements wrapping com-radio
       * @type {String}
       */
      selectable: {
        type: String,
        value: 'com-radio'
      },

      /**
       * @desc determine if we should observe current change
       * @type {Boolean}
       */
      _observe: {
        type: Boolean,
        value: true
      },

      /**
       * @desc selected listener
       * @type {Function}
       */
      _updateSelectedListener: {
        type: Function
      },

      /**
       * @desc Allow selectables in nested doms
       * @type {Boolean}
       */
      allowNestedSelectables: {
        type: Boolean,
        value: false
      }
    };
  }

  ready() {
    super.ready();
    this._updateSelectedListener = this._updateSelected.bind(this);
  }

  /**
   * @desc attach even listeners and set initial values
   * @param {Event} event
   */
  _updateSlotted(event) {
    let nodes = this.$.slot.assignedNodes({flatten: true})
      .filter(n => n.nodeType === Node.ELEMENT_NODE);

    nodes = (this.allowNestedSelectables) ?
      this._getNodes(nodes) :
      nodes.filter(n => n.nodeName === this.selectable.toUpperCase());

    this.items = nodes;
    nodes.forEach(node => {
      // set initial value
      if (node.getAttribute(this.attrForSelected) === this.selected) {
        node[this._getCamelCase(this.selectedAttribute)] = true;
      }
      // set listener
      let eventName = this.selectedAttribute + '-changed';
      node.removeEventListener(eventName, this._updateSelectedListener); // remove first incase already applied
      node.addEventListener(eventName, this._updateSelectedListener);
    });
  }

  /**
   * @desc Search through nodes and child nodes to get all selectable elements
   * @param {Object[]} baseNodes
   * @return {Object[]} list of selectables
   */
  _getNodes(baseNodes) {
    let nodes = [];

    baseNodes.forEach(item => {
      if (item.nodeName === this.selectable.toUpperCase()) {
        nodes.push(item);
      } else if (item.childNodes) {
        nodes = nodes.concat(this._getNodes(item.childNodes));
      }
    });
    return nodes;
  }

  /**
   * @desc get camel case from hyphenated string
   * @param {String} hyphenated id
   * @return {String} camelcase id
   */
  _getCamelCase(id) {
    return id.replace(/-([a-z])/g, function (g) { return g[1].toUpperCase(); });
  }

  /**
   * @desc update selected radio group
   * @param {Event} event
   */
  _updateSelected(event) {

    if (!this._observe) return;

    let path = event.path || (event.composedPath && event.composedPath());
    if (!path) return;

    let radio = path.filter(node => node.nodeName === this.selectable.toUpperCase())[0],
      selected = radio[this._getCamelCase(this.selectedAttribute)],
      value = radio.getAttribute(this.attrForSelected);

    if (this.selected) {
      // prevent deselect
      let oldItem = this._getElementFromValue(this.selected);
      if (this.selected === value && oldItem) {
        oldItem[this._getCamelCase(this.selectedAttribute)] = true;
        return;
      }
      // unselect previous item
      if (oldItem) {
        this._observe = false; // prevent observers from triggering _updateSelected when unselecting radio
        oldItem[this._getCamelCase(this.selectedAttribute)] = false;
        this._observe = true;
      }
    }

    this.selected = selected ? value : '';
  }

  /**
   * @desc get element from value
   * @param {String} value
   * @return {Element}
   */
  _getElementFromValue(value) {
    return this.items.filter(item => item.getAttribute(this.attrForSelected) === value)[0];
  }
}
window.customElements.define(COMRadioGroup.is, COMRadioGroup);
